import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, TrendingUp, Users, LineChart, Globe } from 'lucide-react';

export default function MerchantInsights() {
  // Live data states
  const [bestSellers, setBestSellers] = useState([]);
  const [cvCompetitors, setCvCompetitors] = useState([]);
  const [cvTopMerchants, setCvTopMerchants] = useState([]);
  const [topicTrends, setTopicTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAbortErr = (e) => {
    if (!e) return false;
    const name = e.name || '';
    const msg = e.message || String(e) || '';
    return name === 'AbortError' || msg.includes('AbortError') || msg.includes('signal is aborted');
  };

  const fetchSafe = async (url, options) => {
    try {
      const r = await fetch(url, options);
      return r;
    } catch (e) {
      if (isAbortErr(e)) return null;
      throw e;
    }
  };

  // Helper to get brand id from storage
  const getBrandId = () => {
    return (
      localStorage.getItem('selectedBrandId') ||
      localStorage.getItem('currentBrandId') ||
      sessionStorage.getItem('selectedBrandId') ||
      ''
    );
  };

  // Fetch from backend read endpoints (to be implemented on server)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        if (alive) setLoading(true);
        if (alive) setError('');

        const brandId = getBrandId();
        const headers = {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        };

        // Date range: last 30 days by default
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        const toISO = (d) => d.toISOString().split('T')[0];
        const startDate = toISO(start);
        const endDate = toISO(end);
        const country = 'TR';

        // DEMO MODE: fill all sections with dummy data for specific brand(s)
        const demoBrandIds = new Set([
          '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a',
          'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4'
        ]);
        if (demoBrandIds.has(brandId)) {
          const demoBest = Array.from({ length: 20 }, (_ , i) => ({
            rank: i + 1,
            title: `Demo Bestseller ${String.fromCharCode(65 + (i % 26))}`,
            brand: ['Alpha','Beta','Gamma','Delta','Omega'][i % 5],
            category: ['Ev Dekorasyonu > Halı & Kilim','Elektronik > Kulaklık','Moda > Ayakkabı','Spor > Aksesuar','Mutfak > Küçük Ev Aletleri'][i % 5],
            relativeDemand: ['HIGH','MEDIUM','LOW','MEDIUM','HIGH'][i % 5],
          }));
          const demoComp = Array.from({ length: 12 }, (_ , i) => ({
            rank: i + 1,
            domain: i === 2 ? 'yourbrand.com' : `competitor${i+1}.com`,
            relativeVisibility: Math.max(0.05, 0.6 - i * 0.035),
            higherPositionRate: Math.max(0.1, 0.5 - i * 0.03),
            pageOverlapRate: Math.max(0.05, 0.45 - i * 0.028),
            isYourDomain: i === 2,
          }));
          const demoTopMerchants = Array.from({ length: 12 }, (_ , i) => ({
            rank: i + 1,
            domain: `merchant${i+1}.com`,
            pageOverlapRate: Math.max(0.05, 0.55 - i * 0.03),
            adsOrganicRatio: 0.3 + (i % 5) * 0.08,
            isYourDomain: i === 4,
          }));
          const topics = ['Kamp Çadırı','Koşu Ayakkabısı','Robot Süpürge','Espresso Makinesi','Bebek Bezi','Akıllı Saat','Bluetooth Kulaklık','Protein Tozu','Makyaj Seti','Bisiklet','Notebook','Spor Ayakkabı'];
          const demoTrends = topics.map((t, idx) => {
            const base = 0.3 + (idx % 6) * 0.08;
            return {
              customerCountryCode: 'TR',
              topic: t,
              searchInterest: Math.min(0.95, base),
              last7: Math.min(0.95, base + 0.02 * ((idx % 3) - 1)),
              last30: Math.min(0.95, base + 0.04 * ((idx % 4) - 1.5)),
              last90: Math.min(0.95, base + 0.06 * ((idx % 5) - 2)),
              last120: Math.min(0.95, base + 0.08 * ((idx % 6) - 3)),
            };
          });
          if (alive) {
            setBestSellers(demoBest);
            setCvCompetitors(demoComp);
            setCvTopMerchants(demoTopMerchants);
            setTopicTrends(demoTrends);
            setLoading(false);
          }
          return;
        }

        // Topic Trends
        try {
          const r = await fetchSafe(`https://api.glowytics.com/api/google-ads/merchant-center/reports/topic-trends/db?country=${country}&startDate=${startDate}&endDate=${endDate}`, {
            method: 'GET',
            headers,
            credentials: 'include'
          });
          if (r && r.ok) {
            const j = await r.json();
            const rows = Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : [];
            if (rows.length && alive) {
              const mapped = rows.map(x => ({
                customerCountryCode: x.customer_country_code || 'TR',
                topic: x.topic || '',
                // Normalize 0-100 to 0-1 for UI percentage display
                searchInterest: (() => {
                  const v = Number(x.search_interest ?? 0);
                  return isFinite(v) ? (v > 1 ? v / 100 : v) : 0;
                })(),
                last7: (() => {
                  const v = Number(x.last7_days_search_interest ?? 0);
                  return isFinite(v) ? (v > 1 ? v / 100 : v) : 0;
                })(),
                last30: (() => {
                  const v = Number(x.last30_days_search_interest ?? 0);
                  return isFinite(v) ? (v > 1 ? v / 100 : v) : 0;
                })(),
                last90: (() => {
                  const v = Number(x.last90_days_search_interest ?? 0);
                  return isFinite(v) ? (v > 1 ? v / 100 : v) : 0;
                })(),
                last120: (() => {
                  const v = Number(x.last120_days_search_interest ?? 0);
                  return isFinite(v) ? (v > 1 ? v / 100 : v) : 0;
                })(),
              }));
              if (alive) setTopicTrends(mapped.slice(0, 100));
            }
          }
        } catch (_e) { /* ignore to accumulate other sections */ }

        // Best Sellers
        try {
          const r = await fetchSafe(`https://api.glowytics.com/api/google-ads/merchant-center/reports/best-sellers/db?country=${country}&startDate=${startDate}&endDate=${endDate}`, {
            method: 'GET',
            headers,
            credentials: 'include'
          });
          if (r && r.ok) {
            const j = await r.json();
            const rows = Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : [];
            if (rows.length && alive) {
              const mapped = rows.map((x, idx) => ({
                rank: Number(x.rank || idx + 1),
                title: x.title || '',
                brand: x.brand || '',
                category: x.category || '',
                relativeDemand: x.relative_demand || 'MEDIUM'
              }));
              if (alive) setBestSellers(mapped.slice(0, 50));
            }
          }
        } catch (_e) { /* ignore */ }

        // CV Competitors
        try {
          const r = await fetchSafe(`https://api.glowytics.com/api/google-ads/merchant-center/reports/competitive-visibility/competitors/db?country=${country}&startDate=${startDate}&endDate=${endDate}`, {
            method: 'GET',
            headers,
            credentials: 'include'
          });
          if (r && r.ok) {
            const j = await r.json();
            const rows = Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : [];
            if (rows.length && alive) {
              const mapped = rows.map((x, idx) => ({
                rank: Number(x.rank || idx + 1),
                domain: x.domain || '',
                relativeVisibility: Number(x.relative_visibility ?? 0),
                higherPositionRate: Number(x.higher_position_rate ?? 0),
                pageOverlapRate: Number(x.page_overlap_rate ?? 0),
                isYourDomain: Boolean(x.is_your_domain ?? false)
              }));
              if (alive) setCvCompetitors(mapped.slice(0, 50));
            }
          }
        } catch (_e) { /* ignore */ }

        // CV Top Merchants
        try {
          const r = await fetchSafe(`https://api.glowytics.com/api/google-ads/merchant-center/reports/competitive-visibility/top-merchants/db?country=${country}&startDate=${startDate}&endDate=${endDate}`, {
            method: 'GET',
            headers,
            credentials: 'include'
          });
          if (r && r.ok) {
            const j = await r.json();
            const rows = Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : [];
            if (rows.length && alive) {
              const mapped = rows.map((x, idx) => ({
                rank: Number(x.rank || idx + 1),
                domain: x.domain || '',
                pageOverlapRate: Number(x.page_overlap_rate ?? 0),
                adsOrganicRatio: Number(x.ads_organic_ratio ?? 0),
                isYourDomain: Boolean(x.is_your_domain ?? false)
              }));
              if (alive) setCvTopMerchants(mapped.slice(0, 50));
            }
          }
        } catch (_e) { /* ignore */ }

      } catch (e) {
        if (isAbortErr(e)) {
          // ignore aborts
        } else {
          if (alive) setError('Failed to load Merchant Insights data.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KPIs
  const kpi = useMemo(() => {
    const categories = new Set(bestSellers.map(x => x.category));
    const youRank = cvCompetitors.find(x => x.isYourDomain)?.rank || null;
    const avgRelVis = (cvCompetitors.reduce((s, x) => s + x.relativeVisibility, 0) / cvCompetitors.length) || 0;
    return {
      bestSellerCount: bestSellers.length,
      categoriesCount: categories.size,
      competitorsCount: cvCompetitors.length,
      yourRank: youRank,
      avgVisibility: avgRelVis,
      topicsCount: topicTrends.length,
    };
  }, [bestSellers, cvCompetitors, topicTrends]);

  // Removed KPI cards per new design

  const MiniTrend = ({ data }) => {
    // simple inline area for relativeVisibility trend using dummy values
    const width = 280, height = 80, pad = 6;
    const vals = data.map(d => d.relativeVisibility);
    const maxY = Math.max(0.001, ...vals);
    const x = (i) => pad + (i * (width - 2*pad)) / Math.max(1, data.length - 1);
    const y = (v) => height - pad - (v * (height - 2*pad)) / maxY;
    const path = data.map((d, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(d.relativeVisibility)}`).join(' ');
    return (
      <svg width={width} height={height} className="text-indigo-600">
        <path d={path} fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  };

  // More explicit visualization: top competitors by relative visibility (bars)
  const RelVisBars = ({ data, limit = 7 }) => {
    try {
      const items = [...data]
        .sort((a, b) => b.relativeVisibility - a.relativeVisibility)
        .slice(0, limit);
      const max = Math.max(0.001, ...items.map(i => i.relativeVisibility));
      return (
        <div className="min-w-[280px]">
          <div className="text-[11px] text-gray-500 mb-2">
            Relative visibility = share of overlapping impressions you have vs each domain.
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => {
              const pct = Math.max(0, Math.min(1, it.relativeVisibility));
              const isYou = !!it.isYourDomain;
              return (
                <div key={`${it.domain}-${idx}`}>
                  <div className="flex justify-between text-[11px] text-gray-600 mb-1">
                    <span className={`${isYou ? 'font-medium text-indigo-700' : ''}`}>{it.domain}{isYou ? ' (you)' : ''}</span>
                    <span className={`${isYou ? 'font-semibold text-indigo-700' : 'text-gray-700'}`}>{(pct*100).toFixed(1)}%</span>
                  </div>
                  <div className="h-2.5 w-full bg-gray-100 rounded">
                    <div
                      className={`h-2.5 rounded ${isYou ? 'bg-indigo-500' : 'bg-gray-300'}`}
                      style={{ width: `${(pct/max)*100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            Tip: Higher position rate and overlap help explain why a domain wins visibility.
          </div>
        </div>
      );
    } catch {
      return null;
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {loading && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
            Loading Merchant Insights...
          </div>
        )}
        {!!error && !loading && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
            {error}
          </div>
        )}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <img src="/google-merchant-centerpngg.svg" alt="Merchant Center" className="h-6 w-auto" />
            <h1 className="text-xl font-semibold text-gray-900">Merchant Insights</h1>
          </div>
          <div className="text-sm text-gray-500">Top Merchants, Competitive Visibility, Best Sellers, Topic Trends</div>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top merchants */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <Users className="w-4 h-4 text-indigo-600" />
                <span>Top Merchants</span>
              </div>
              <div className="text-xs text-gray-500">Category: Apparel</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead><tr className="bg-gray-50"><th className="px-2 py-1">rank</th><th className="px-2 py-1">domain</th><th className="px-2 py-1">overlap</th><th className="px-2 py-1">ads/organic</th></tr></thead>
                <tbody>
                  {cvTopMerchants.map((t,i)=>(
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1 text-right">{t.rank}</td>
                      <td className="px-2 py-1">{t.domain}{t.isYourDomain?' (you)':''}</td>
                      <td className="px-2 py-1 text-right">{(t.pageOverlapRate*100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(t.adsOrganicRatio*100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competitive Visibility - quick glance */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <LineChart className="w-4 h-4 text-indigo-600" />
                <span>Competitive Visibility (quick glance)</span>
              </div>
              <div className="text-xs text-gray-500">Traffic: ADS • TR</div>
            </div>
            <div className="flex items-start gap-6">
              <RelVisBars data={cvCompetitors} />
              <div className="flex-1 overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-1">rank</th>
                      <th className="px-2 py-1">domain</th>
                      <th className="px-2 py-1">relVis %</th>
                      <th className="px-2 py-1">higher %</th>
                      <th className="px-2 py-1">overlap %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cvCompetitors.map((c,i)=>(
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 text-right">{c.rank}</td>
                        <td className="px-2 py-1">{c.domain}{c.isYourDomain?' (you)':''}</td>
                        <td className="px-2 py-1 text-right">{(c.relativeVisibility*100).toFixed(1)}%</td>
                        <td className="px-2 py-1 text-right">{(c.higherPositionRate*100).toFixed(1)}%</td>
                        <td className="px-2 py-1 text-right">{(c.pageOverlapRate*100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Best Sellers */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <ShoppingCart className="w-4 h-4 text-indigo-600" />
                <span>Best Sellers (snapshot)</span>
              </div>
              <div className="text-xs text-gray-500">Country: TR • Weekly</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead><tr className="bg-gray-50">
                  <th className="px-2 py-1">rank</th>
                  <th className="px-2 py-1">title</th>
                  <th className="px-2 py-1">brand</th>
                  <th className="px-2 py-1">category</th>
                  <th className="px-2 py-1">relative</th>
                </tr></thead>
                <tbody>
                  {bestSellers.map((r,i)=>(
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1 text-right">{r.rank}</td>
                      <td className="px-2 py-1">{r.title}</td>
                      <td className="px-2 py-1">{r.brand}</td>
                      <td className="px-2 py-1">{r.category}</td>
                      <td className="px-2 py-1">{r.relativeDemand}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Topic trends */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <Globe className="w-4 h-4 text-indigo-600" />
                <span>Topic Trends</span>
              </div>
              <div className="text-xs text-gray-500">Last 7 days • TR</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1">country</th>
                    <th className="px-2 py-1">topic</th>
                    <th className="px-2 py-1">interest</th>
                    <th className="px-2 py-1">last 7</th>
                    <th className="px-2 py-1">last 30</th>
                    <th className="px-2 py-1">last 90</th>
                    <th className="px-2 py-1">last 120</th>
                  </tr>
                </thead>
                <tbody>
                  {topicTrends.map((r,i)=>(
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1">{r.customerCountryCode}</td>
                      <td className="px-2 py-1">{r.topic}</td>
                      <td className="px-2 py-1 text-right">{(r.searchInterest*100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last7*100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last30*100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last90*100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last120*100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


